import { useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { TGetUserTicketsResponse } from 'types/raffle';
import { TResponse } from 'types/axios';
import { getUserTicketsService } from 'pages/Raffle/services/raffle';
import { DEFAULT_USER_TICKETS_FILTER } from 'pages/Raffle/constants';
import { RaffleTicketsFilter, TUserTicketItem } from '../../types/common.types';

type GetTickets = {
  isQueryingGetTickets: boolean;
  userTickets: TUserTicketItem[] | undefined;
  getUserRaffleTickets: () => void;
  hasErrorQueryingTickets: boolean;
  getTicketsErrorMessage: string;
  refetchTicketItems: () => void;
  isFetchingNextPage: boolean;
};

type GetTicketsProps = {
  filter?: RaffleTicketsFilter;
  size: number;
};

function useGetUserTickets({
  filter = DEFAULT_USER_TICKETS_FILTER,
  size,
}: GetTicketsProps): GetTickets {
  const [error, setError] = useState({ hasError: false, errorMessage: '' });

  const {
    data,
    refetch: refetchTicketItems,
    fetchNextPage: fetchNextUserTicketItems,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ['getUserTickets', filter],
    ({ pageParam = undefined }) => {
      return getUserTicketsService({
        filter,
        page: pageParam,
        size,
      });
    },
    {
      enabled: false,
      suspense: false,
      getNextPageParam: (_, pages) =>
        pages[pages.length - 1].data.result?.userDraws.length >= size &&
        pages.length,
      onSuccess: (resp) => {
        if (!resp.pages.slice(-1)[0].data.success) {
          setError({
            hasError: true,
            errorMessage: resp.pages.slice(-1)[0].data.errorMessage,
          });
        } else {
          setError({ hasError: false, errorMessage: '' });
        }
      },
    }
  );

  function getUserRaffleTickets() {
    if (hasNextPage || !data) {
      fetchNextUserTicketItems();
    } else if (error.hasError) {
      refetchTicketItems();
    }
  }

  return {
    isQueryingGetTickets: isLoading,
    userTickets: data?.pages.reduce(
      (acc: TUserTicketItem[], current: TResponse<TGetUserTicketsResponse>) =>
        current.data?.result ? acc.concat(current.data.result.userDraws) : acc,
      []
    ) as TUserTicketItem[],
    getUserRaffleTickets,
    hasErrorQueryingTickets: error.hasError,
    getTicketsErrorMessage: error.errorMessage,
    refetchTicketItems,
    isFetchingNextPage,
  };
}

export default useGetUserTickets;
